import { motion, useScroll, useTransform } from "framer-motion";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  LucideIcon,
  Palette,
  Plane,
  Ruler,
} from "lucide-react";
import { useRef } from "react";

export const AboutPage = () => {
  return (
    <div className="bg-white">
      <HeroSection />
      <NarrativeSection />
      <AirsideAdvantage />
      <DesignStudio />
      <TimelineSection />
      <ManifestoSection />
    </div>
  );
};

// 1. Hero Section: The Authority Statement
const HeroSection = () => {
  return (
    <section className="relative flex flex-col md:flex-row h-screen">
      {/* Left Side: Visual */}
      <div className="w-full md:w-1/2 h-1/2 md:h-full relative overflow-hidden">
        {/* Fallback Image layer if video fails to load or for this demo */}
        <img
          src="images/about-hero.png"
          alt="Runway Construction"
          className="absolute inset-0 w-full h-full object-cover grayscale brightness-50"
        />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Right Side: Text */}
      <div className="w-full md:w-1/2 h-1/2 md:h-full bg-neutral-900 text-white flex flex-col justify-center px-8 md:px-16 lg:px-24 relative overflow-hidden">
        {/* Watermark */}
        <div className="absolute top-[10%] right-[-10%] text-[20rem] font-bold text-white opacity-[0.03] select-none pointer-events-none leading-none font-serif">
          25
        </div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative z-10">
          <h1 className="font-serif text-5xl lg:text-7xl mb-6 leading-tight">
            25 Years of <br />
            <span className="text-neutral-500">Precision.</span>
          </h1>
          <div className="w-20 h-1 bg-primary mb-8" />
          <p className="text-xl md:text-2xl font-light text-neutral-400 leading-relaxed max-w-lg">
            From critical airside infrastructure to world-class aviation civil
            and interior spaces. We are the silent forces behind India’s busiest
            terminals.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// 2. The Introduction: The Narrative (Scroll-Triggered)
const NarrativeSection = () => {
  const text =
    "Arihant Infracon is a well-established engineering organization known for the execution of complex civil, structural, façade, and MEP works. ";
  const highlight =
    "Mission-critical works in live operational airport environments.";

  // We split the text to animate opacity based on scroll
  // For simplicity in this demo, we'll use a reveal effect on view

  return (
    <section className="py-32 px-6 md:px-12 lg:px-24 bg-white">
      <div className="max-w-4xl mx-auto text-center">
        <p className="font-serif text-3xl md:text-5xl leading-tight text-neutral-300">
          <span className="text-neutral-900 transition-colors duration-700 hover:text-neutral-900 cursor-default">
            {text}
          </span>
          <motion.span
            initial={{ color: "#d4d4d4" }}
            whileInView={{ color: "#000000" }}
            viewport={{ margin: "-20% 0px -20% 0px" }}
            transition={{ duration: 1 }}
            className="font-semibold block mt-4">
            {highlight}
          </motion.span>
        </p>
      </div>
    </section>
  );
};

// 3. Feature Section: 'The Airside Advantage' (Dark Mode)
const AirsideAdvantage = () => {
  return (
    <section className="bg-neutral-950 text-white py-24 px-6 md:px-12 lg:px-24 border-t border-neutral-900 relative overflow-hidden">
      {/* Tech Grid Background */}
      <div
        className="absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left: Image/Technical Drawing */}
        <div className="relative">
          <div className="aspect-4/5 bg-neutral-600 rounded-sm overflow-hidden relative border border-neutral-800">
            <img
              src="images/about/airplane-flying-buildings.jpg"
              alt="Airside Operations"
              className="w-full h-full object-cover opacity-180 mix-blend-overlay hover:mix-blend-normal transition-all duration-700 hover:scale-105 hover:opacity-100"
            />
            <div className="absolute bottom-4 left-4 p-2 bg-black/60 backdrop-blur-md border border-white/10 text-xs font-mono text-primary">
              REV 2.0 // AIRSIDE-TECH
            </div>
          </div>
          {/* Decorative Diagram Lines */}
          <svg
            className="absolute -top-8 -left-8 w-24 h-24 text-neutral-800"
            viewBox="0 0 100 100">
            <path
              d="M0 100 L0 0 L100 0"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
            />
          </svg>
        </div>

        {/* Right: Content */}
        <div>
          <div className="flex items-center gap-2 mb-6 text-primary">
            <Plane className="w-6 h-6" />
            <span className="text-xs font-mono tracking-widest uppercase">
              The Airside Advantage
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-serif mb-12">
            Operating at the Intersection of Aviation & Engineering Excellence.
          </h2>

          <div className="space-y-8">
            <FeaturePoint
              icon={AlertTriangle}
              title="NOTAM Expertise"
              desc="Executing time-sensitive works under strict Notice to Airmen (NOTAM) conditions without delays."
            />
            <FeaturePoint
              icon={Clock}
              title="Airside Maintenance"
              desc="7 years of continuous service in Airside Pavement AMC."
            />
            <FeaturePoint
              icon={CheckCircle2}
              title="Zero Disruption"
              desc="Seamless execution in live operational environments with minimal disruption to terminal flow."
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const FeaturePoint = ({
  icon: Icon,
  title,
  desc,
}: {
  icon: LucideIcon;
  title: string;
  desc: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="flex gap-6 group">
    <div className="mt-1">
      <div className="w-12 h-12 bg-neutral-900 border border-neutral-800 rounded-full flex items-center justify-center group-hover:border-primary transition-colors duration-300">
        <Icon className="w-5 h-5 text-neutral-400 group-hover:text-white transition-colors" />
      </div>
    </div>
    <div>
      <h3 className="text-xl font-medium mb-2 text-neutral-200 group-hover:text-white transition-colors">
        {title}
      </h3>
      <p className="text-neutral-500 font-light leading-relaxed group-hover:text-neutral-400 transition-colors">
        {desc}
      </p>
    </div>
  </motion.div>
);

// 4. Feature Section: 'In-House Design Studio' (Light Mode)
const DesignStudio = () => {
  return (
    <section className="bg-neutral-50 py-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-16">
        {/* Content Box */}
        <div className="lg:w-1/3 order-2 lg:order-1 sticky top-32 self-start">
          <div className="bg-white p-10 shadow-[0_0_50px_rgba(0,0,0,0.05)] border-l-4 border-primary">
            <Palette
              className="w-10 h-10 text-neutral-400 mb-6"
              strokeWidth={1}
            />
            <h2 className="font-serif text-4xl mb-6 text-neutral-900">
              Design Meets Execution.
            </h2>
            <p className="text-neutral-600 leading-relaxed font-light mb-8">
              Backed by a dedicated in-house engineering and designing setup.
              Our team of architects and technical consultants manages projects
              from design development to final handover.
            </p>
            <div className="flex gap-4">
              <div className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
                Blueprints
              </div>
              <div className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
                Renders
              </div>
              <div className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
                Samples
              </div>
            </div>
          </div>
        </div>

        {/* Masonry Grid Visuals */}
        <div className="lg:w-2/3 order-1 lg:order-2 grid grid-cols-2 gap-4 md:gap-8">
          <div className="space-y-4 md:space-y-8 mt-12">
            <img
              src="images/about/execution.jpg"
              alt="Execution Phase"
              className="w-full aspect-3/4 object-cover grayscale hover:grayscale-0 transition-all duration-700 shadow-md"
            />
            <img
              src="images/about/vision.jpg"
              alt="Technical Drawing"
              className="w-full aspect-square object-cover grayscale hover:grayscale-0 transition-all duration-700 shadow-md"
            />
          </div>
          <div className="space-y-4 md:space-y-8">
            <img
              src="images/about/design.jpg"
              alt="Architectural Design"
              className="w-full aspect-square object-cover grayscale hover:grayscale-0 transition-all duration-700 shadow-md"
            />
            <img
              src="images/about/interior.jpg"
              alt="Interior Render"
              className="w-full aspect-3/4 object-cover grayscale hover:grayscale-0 transition-all duration-700 shadow-md"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// 5. The Timeline: 'A Legacy of Trust'
const TimelineSection = () => {
  const targetRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
  });

  const x = useTransform(scrollYProgress, [0, 1], ["0%", "-75%"]);

  const milestones = [
    {
      year: "2000",
      title: "Inception",
      desc: "Foundation laid in Civil Works.",
      icon: Ruler,
    },
    {
      year: "2010",
      title: "Aviation Entry",
      desc: "Expansion into Aviation Infrastructure.",
      icon: Plane,
    },
    {
      year: "2017",
      title: "Airside Expert",
      desc: "Awarded first Airside Pavement AMC.",
      icon: AlertTriangle,
    },
    {
      year: "2020",
      title: "Luxury Retail",
      desc: "Specialized Interiors Division formed.",
      icon: Palette,
    },
    {
      year: "2025",
      title: "Pan-India",
      desc: "Delivering nationwide excellence.",
      icon: CheckCircle2,
    },
  ];

  return (
    <section
      ref={targetRef}
      className="relative h-[300vh] bg-neutral-900 border-t border-neutral-800">
      <div className="sticky top-0 flex h-screen items-center overflow-hidden">
        <div className="p-12 mb-auto mt-24 absolute z-20">
          <h2 className="text-white text-3xl font-serif">A Legacy of Trust</h2>
          <div className="w-16 h-1 bg-primary mt-4" />
        </div>

        <motion.div style={{ x }} className="flex gap-24 px-12 md:px-24">
          {milestones.map((item, i) => (
            <div
              key={i}
              className="shrink-0 w-[80vw] md:w-[40vw] flex flex-col justify-center relative group">
              <div className="text-[8rem] md:text-[12rem] leading-none font-bold text-neutral-800 group-hover:text-neutral-700 transition-colors select-none font-serif">
                {item.year}
              </div>
              <div className="ml-4 -mt-12 md:-mt-20 relative z-10">
                <item.icon
                  className="w-12 h-12 text-primary mb-6"
                  strokeWidth={1}
                />
                <h3 className="text-3xl md:text-5xl text-white font-serif mb-4">
                  {item.title}
                </h3>
                <p className="text-neutral-400 text-xl max-w-md">{item.desc}</p>
              </div>
              {/* Connector Line */}
              <div className="absolute top-1/2 left-0 w-full h-px bg-neutral-800 -z-10" />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// 6. The Manifesto (Closing)
const ManifestoSection = () => {
  return (
    <section className="py-32 px-6 bg-white text-center">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}>
          <p className="font-serif text-2xl md:text-4xl text-neutral-900 leading-snug">
            "We continue to strive for larger responsibilities, committed to
            raising execution standards in every project we touch."
          </p>
          <div className="mt-12 flex justify-center">
            <svg
              className="w-64 text-primary"
              viewBox="0 0 300 80"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M20 50C50 40 80 60 110 30C120 20 150 70 180 50C210 30 240 50 280 40"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <text
                x="50%"
                y="75"
                textAnchor="middle"
                fill="currentColor"
                fontFamily="serif"
                fontSize="14"
                className="tracking-widest uppercase"
                stroke="none">
                Arihant Infracon Team
              </text>
            </svg>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
